import { ChangeDetectionStrategy, Component, Injector, Optional, Provider, SkipSelf, ViewEncapsulation, computed, effect, forwardRef, inject, input, linkedSignal, runInInjectionContext, signal, untracked, viewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { rxResource } from '@angular/core/rxjs-interop';
import { ActivityEntityName, Entity } from '@jms/model';
import { filterSuccessResult } from '@ngneat/query';
import { EntityDataService, EntityManifestService, EntitySurfaceFactory } from '@workbuddy/services/entities';
import { MetadataService } from '@workbuddy/ui/services/metadata.service';
import { WbSurfaceComponent, AnyWbSurfaceDefinition } from '@workbuddy/ui/v2/surface.engine';
import { withEntityRelations } from '@workbuddy/ui/v2/surface.engine/core/surface/with.entity.relations';
import { SurfaceMountMemory } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.mount.memory';
import type { RelationBoardBuilder } from '@workbuddy/ui/v2/surface.engine/core/contracts/relation.board';
import { firstValueFrom, map } from 'rxjs';

import { DetailPaneContext } from './context/detail.pane.context';
import { DetailPaneNavigation } from './detail.pane.navigation';
import { DETAIL_PANE_BACK, detailPaneBackFor } from './detail.pane.back';
import { SurfaceDeactivateGuard } from '../entity.inspector/surface.deactivate.guard';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { detailPaneBridgeStyles, detailPaneStyleId } from './detail.pane.theme';

/**
 * Publishes Back for a child pane's surface, so the child's header can render it
 * leftmost on the title row instead of the pane stacking a Back row above the surface.
 *
 * `SkipSelf` deliberately: the navigation that owns the return trip is the ENCLOSING pane's, never
 * this pane's own (which drives this pane's own children). A root pane resolves nothing above it, so
 * the token stays null there and the title row renders no Back at all.
 */
function provideDetailPaneBack(): Provider {

  return {
    provide: DETAIL_PANE_BACK,
    useFactory: detailPaneBackFor,
    deps: [[new SkipSelf(), new Optional(), DetailPaneNavigation]],
  };
}

/**
 * Host for per-entity detail panes. Given a context (entity + id or value) it loads the record when
 * needed, then mounts the entity's `preview` surface as <wb-surface>.
 *
 * The definition is built once per mount identity — factory, relations, readonly and host params —
 * because a factory bakes those in and no model swap reaches them. What happens when only the
 * focused record changes depends on the preview operation's `supportsModelSwap`: opted in, the
 * mounted surface is handed the new model and survives; otherwise the definition is rebuilt and
 * <wb-surface> remounts, which is the safe default for factories that capture their record.
 * Renderers own the entire pane layout; this host owns only data loading and load/empty/error states.
 * Routing-free by design so it works in board panels, dialogs and full pages alike.
 */
@Component({
  selector: 'detail-pane',
  templateUrl: './detail.pane.component.html',
  styleUrls: ['./detail.pane.component.less'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [WbSurfaceComponent, forwardRef(() => DetailPaneComponent)],
  // Mount memory: a preview without model swap remounts per record, and the next record must open with the same chrome.
  providers: [DetailPaneNavigation, provideDetailPaneBack(), SurfaceMountMemory],
})
export class DetailPaneComponent {

  private readonly entityService = inject(EntityDataService);
  private readonly manifestService = inject(EntityManifestService);
  private readonly metadataService = inject(MetadataService);
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);
  protected readonly navigation = inject(DetailPaneNavigation);
  private readonly $childPane = viewChild<DetailPaneComponent>('childPane');
  private readonly leaveGuard = new SurfaceDeactivateGuard(() => this.$surfaceDefinition()?.$surface() ?? null, this.injector);

  public readonly $context = input.required<DetailPaneContext>({ alias: 'context' });
  private readonly $recordKey = computed(() => `${this.$context().entity}:${this.$context().id ?? (this.$context().value as Entity | undefined)?._id ?? ''}`);

  // Resolved preview factory for the current entity.
  private readonly $surfaceFactory = signal<EntitySurfaceFactory | null>(null);
  private readonly $relations = signal<Readonly<Record<string, RelationBoardBuilder>>>({});

  protected readonly $entity = computed<ActivityEntityName>(() => this.$context().entity);

  // Compared by entity and id: a board refetch hands the same target in a new context, and a new request object refetched the record and remounted the surface mid-upload (WBS-12090).
  private readonly $recordRequest = computed(() => {
    const context = this.$context();
    if (context.value != null || !context.id) {
      return undefined;
    }
    return { entity: context.entity, id: context.id };
  }, { equal: (a, b) => a?.entity === b?.entity && a?.id === b?.id });

  private readonly $recordResource = rxResource({
    params: () => this.$recordRequest(),
    stream: ({ params }) => {
      // The preview's read-time extras (e.g. a financial document's payment options) arrive with its record.
      return this.entityService
        .getById<Entity>(params.entity, params.id, this.manifestService.operationReadOptions(params.entity, 'preview'))
        .result$
        .pipe(
          filterSuccessResult(),
          map(result => result.data?.item ?? null)
        );
    },
  });

  // Keep the previous record on screen while the next one loads so the mounted renderer subtree
  // doesn't flash empty between focus changes.
  private readonly $loadedRecord = linkedSignal<Entity | null, Entity | null>({
    source: () => this.$recordResource.value() ?? null,
    computation: (next, prev) => next ?? prev?.value ?? null,
  });

  // Null when nothing is selected (no keep-previous leak): deselecting must unmount the renderer
  // and fall back to the empty state, not keep showing the last record.
  protected readonly $record = computed<unknown>(() => {

    const context = this.$context();
    if (context.value != null) return context.value;
    if (!context.id) return null;
    return this.$loadedRecord();
  });
  private readonly $hasRecord = computed(() => this.$record() != null);
  protected readonly $showEmpty = computed(() => this.$context().value == null && !this.$context().id);
  protected readonly $showLoading = computed(() => !this.$showEmpty() && this.$record() == null && this.$recordResource.isLoading());
  protected readonly $showError = computed(() => !this.$showEmpty() && this.$record() == null && this.$recordResource.error() != null);

  // Mount identity. A change here MUST rebuild the definition: the factory runs once and bakes in
  // the readonly flag, the host params and every service it injected, none of which a model swap
  // reaches. The focused record is deliberately absent — that is what `supportsModelSwap` decides.
  // Compared by part: a board refetch hands an equal context in a new object, and rebuilding on it remounted the surface mid-upload (WBS-12090).
  private readonly $mountKey = computed(() => {

    const context = this.$context();
    return { factory: this.$surfaceFactory(), relations: this.$relations(), readonly: context.readonly === true, params: context.params };
  }, { equal: (a, b) => a.factory === b.factory && a.relations === b.relations && a.readonly === b.readonly && a.params === b.params });

  // Whether this entity's preview factory declared that it can survive a record swap. Off unless the
  // manifest opts in: a factory that captured the first record in its persistence, schema, or
  // readonly derivation would otherwise write the new record's edits to the old one.
  private readonly $supportsModelSwap = computed(() => {

    const operation = this.manifestService.resolveOperation(this.$entity(), 'preview');
    return operation?.kind === 'surface' && operation.supportsModelSwap === true;
  });

  // Built once per mount identity. When the flag is on, a record change leaves this untouched and
  // the model is pushed into the live surface by the effect below; <wb-surface> only remounts when
  // the reference actually changes. Built inside this injector because factories capture services.
  protected readonly $surfaceDefinition = computed<AnyWbSurfaceDefinition | null>(() => {

    const { factory, relations, readonly, params } = this.$mountKey();
    // Swap mode still tracks the transition into and out of "no record": the first record has to
    // build the definition, and deselecting has to drop it. Only record-to-record is untracked.
    const record = this.$supportsModelSwap() && this.$hasRecord() ? untracked(() => this.$record()) : this.$record();
    if (!factory || record == null) return null;
    return runInInjectionContext(this.injector, () => withEntityRelations(factory(record, { operation: 'preview', readonly, params }), relations));
  });

  /** An upload is in flight here or in a child pane; a host that swaps this pane's record must run `canDeactivate` first. */
  public readonly $busy = computed<boolean>(() => this.$surfaceDefinition()?.$surface()?.$busy() === true || this.$childPane()?.$busy() === true);

  constructor() {

    DesignSystem.Mount(this.document, detailPaneStyleId, detailPaneBridgeStyles);
    this.navigation.registerLeaveGuard(() => this.canDeactivate());
    effect(() => {
      this.$recordKey();
      untracked(() => this.navigation.reset());
    });
    effect(() => {
      const entity = this.$entity();
      untracked(() => this.resolveSurfaces(entity));
    });
    effect(() => {
      const record = this.$record();
      const definition = this.$surfaceDefinition();
      if (!this.$supportsModelSwap() || record == null || !definition) return;
      untracked(() => this.swapMountedModel(definition, record));
    });
  }

  /**
   * Pushes a newly focused record into the surface that is already mounted, so a click down the
   * board keeps the header, tabs and any embedded board alive instead of remounting them.
   *
   * A true replace, not a merge: the incoming record is a different one, so binds it does not carry
   * must read empty rather than inheriting the previous record's values.
   */
  private swapMountedModel(definition: AnyWbSurfaceDefinition, record: unknown): void {

    const surface = definition.$surface();
    // No facade yet means <wb-surface> has not finished mounting this definition; the model it is
    // mounting with is already the current record, so there is nothing to swap.
    if (!surface) return;
    if (surface.$model() === record) return;
    // A board response can omit optional binds (for example an unassigned Visit's job).
    // Reset from the schema skeleton so mounted Signal Forms fields cannot become orphaned.
    surface.reset({ ...(record as Record<string, unknown>) });
  }

  public async canDeactivate(): Promise<boolean> {

    const child = this.$childPane();
    if (child && !await child.canDeactivate()) return false;
    return this.leaveGuard.canDeactivate('navigate');
  }

  private async resolveSurfaces(entity: ActivityEntityName): Promise<void> {

    this.$surfaceFactory.set(null);
    this.$relations.set({});

    if (!this.manifestService.supportsOperation(entity, 'preview')) return;

    await firstValueFrom(this.metadataService.getMetadata(entity));
    if (this.$entity() !== entity) return;
    // Same contract as manage host: schema relation tabs resolve via manifest.relations.
    const [factory, relations] = await Promise.all([
      this.manifestService.loadSurface(entity, 'preview'),
      this.manifestService.loadRelations(entity),
    ]);
    if (this.$entity() !== entity) return;
    if (!factory) return;

    this.$relations.set(relations);
    this.$surfaceFactory.set(factory);
  }
}
